import React, { useState } from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { canAskAI, getRemainingAI, incrementAICount, isPro } from '../utils/featureGate';
import StreamingText from '../components/StreamingText';
import { useIsMobile } from '../hooks/useWindowWidth';
import EmailCapture from '../components/EmailCapture';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function Ask() {
  const isMobile = useIsMobile();
  const styles = {
    container: {
      background: '#0f172a',
      color: '#f8fafc',
      minHeight: '100vh',
      padding: isMobile ? '48px 16px' : '60px 40px',
      maxWidth: '800px',
      margin: '0 auto',
      position: 'relative',
    },
    glowOrb: {
      position: 'absolute',
      top: '-60px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '600px',
      height: '400px',
      background: 'radial-gradient(ellipse at center, rgba(56, 189, 248, 0.07) 0%, transparent 65%)',
      pointerEvents: 'none',
      zIndex: 0,
    },
    header: {
      position: 'relative',
      zIndex: 1,
      marginBottom: '40px',
    },
    title: {
      fontSize: '36px',
      fontWeight: '800',
      marginBottom: '8px',
      letterSpacing: '-0.5px',
    },
    subtitle: {
      color: '#64748b',
      fontSize: '16px',
      margin: 0,
    },
    card: {
      background: '#1e293b',
      borderRadius: '16px',
      padding: '28px',
      marginBottom: '20px',
      border: '1px solid #334155',
      position: 'relative',
      zIndex: 1,
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '16px',
    },
    cardIcon: {
      fontSize: '22px',
    },
    cardTitle: {
      fontSize: '20px',
      fontWeight: '700',
      margin: 0,
    },
    cardDesc: {
      color: '#64748b',
      marginBottom: '16px',
      fontSize: '14px',
      margin: '0 0 16px',
    },
    textarea: {
      width: '100%',
      background: '#0f172a',
      border: '1px solid #334155',
      borderRadius: '10px',
      color: '#f8fafc',
      padding: '14px',
      fontSize: '15px',
      resize: 'vertical',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      lineHeight: 1.6,
      outline: 'none',
    },
    inputRow: {
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
    },
    input: {
      flex: 1,
      background: '#0f172a',
      border: '1px solid #334155',
      borderRadius: '10px',
      color: '#f8fafc',
      padding: '12px 14px',
      fontSize: '15px',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      outline: 'none',
    },
    btn: {
      background: '#38bdf8',
      color: '#0f172a',
      border: 'none',
      padding: '12px 28px',
      borderRadius: '10px',
      fontWeight: '700',
      fontSize: '15px',
      cursor: 'pointer',
      marginTop: '12px',
      display: 'block',
    },
    btnInline: {
      marginTop: 0,
      display: 'inline-block',
      whiteSpace: 'nowrap',
    },
    btnDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    answerBox: {
      marginTop: '20px',
      background: '#0f172a',
      borderRadius: '10px',
      padding: '20px',
      border: '1px solid rgba(56, 189, 248, 0.15)',
    },
    answerLabel: {
      fontSize: '12px',
      color: '#38bdf8',
      fontWeight: '600',
      marginBottom: '10px',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    answer: {
      color: '#e2e8f0',
      lineHeight: 1.8,
      fontSize: '15px',
      whiteSpace: 'pre-wrap',
    },
    suggestions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
    },
    suggestionBtn: {
      background: '#0f172a',
      border: '1px solid #334155',
      color: '#94a3b8',
      padding: '8px 14px',
      borderRadius: '20px',
      cursor: 'pointer',
      fontSize: '13px',
      fontFamily: 'inherit',
      transition: 'border-color 0.2s, color 0.2s',
    },
    legalFooter: { color: '#475569', fontSize: '12px', lineHeight: 1.6, textAlign: 'center', marginTop: '40px', padding: '14px 18px', background: 'rgba(100,116,139,0.06)', border: '1px solid #1e293b', borderRadius: '10px' },
    limitBadge: { fontSize: '12px', fontWeight: '600', marginLeft: 'auto' },
    upgradeLink: { color: '#38bdf8', textDecoration: 'none', fontWeight: '700' },
    proBadge: { fontSize: '12px', color: '#38bdf8', fontWeight: '600', marginLeft: 'auto', background: 'rgba(56,189,248,0.1)', padding: '2px 8px', borderRadius: '6px' },
  };

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const [explanation, setExplanation] = useState('');
  const [jargonLoading, setJargonLoading] = useState(false);
  const [headline, setHeadline] = useState('');
  const [translation, setTranslation] = useState('');
  const [headlineLoading, setHeadlineLoading] = useState(false);
  const pro = isPro();
  const remaining = getRemainingAI();

  const askQuestion = async () => {
    if (!question.trim()) return;
    if (!canAskAI()) {
      setAnswer("You've used all 5 free AI uses for today. Upgrade to Pro for a higher daily limit.");
      return;
    }
    incrementAICount();
    setLoading(true);
    setAnswer('');
    try {
      const response = await fetch(`${API}/api/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      setLoading(false);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setAnswer(prev => prev + decoder.decode(value, { stream: true }));
      }
    } catch {
      setAnswer('Sorry, the AI is unavailable right now. Please try again later.');
      setLoading(false);
    }
  };

  const translateJargon = async () => {
    if (!term.trim()) return;
    if (!canAskAI()) {
      setExplanation("You've used your 5 free AI uses for today. Upgrade to Pro for more.");
      return;
    }
    incrementAICount();
    setJargonLoading(true);
    setExplanation('');
    try {
      const response = await fetch(`${API}/api/jargon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ term }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      setJargonLoading(false);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setExplanation(prev => prev + decoder.decode(value, { stream: true }));
      }
    } catch {
      setExplanation('Sorry, could not translate that term right now.');
      setJargonLoading(false);
    }
  };

  const translateHeadline = async () => {
    if (!headline.trim()) return;
    if (!canAskAI()) {
      setTranslation("You've used your 5 free AI uses for today. Upgrade to Pro for more.");
      return;
    }
    incrementAICount();
    setHeadlineLoading(true);
    setTranslation('');
    try {
      const response = await fetch(`${API}/api/translate-headline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headline }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      setHeadlineLoading(false);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setTranslation(prev => prev + decoder.decode(value, { stream: true }));
      }
    } catch {
      setTranslation('Sorry, could not translate that headline right now.');
      setHeadlineLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      askQuestion();
    }
  };

  return (
    <div style={styles.container}>
      <SEO
        title="Ask an AI Finance Coach — Free Money Questions Answered"
        description="Get instant answers to any money question. Explain financial jargon, translate headlines, get plain-English financial guidance. Free AI finance coach."
        path="/ask"
        keywords="AI finance coach, financial questions, money advice, jargon translator, personal finance AI"
      />
      <div style={styles.glowOrb} />

      <div style={styles.header}>
        <h1 style={styles.title}>Ask the AI Finance Coach</h1>
        <p style={styles.subtitle}>No jargon. No judgment. Just real answers to your money questions.</p>
      </div>

      {/* Ask a question */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardIcon}>🤖</span>
          <h2 style={styles.cardTitle}>Ask Anything</h2>
          {!pro && (
            <span style={{ ...styles.limitBadge, color: remaining <= 1 ? '#f87171' : '#64748b' }}>
              {remaining > 0 ? `${remaining} questions left today` : '0 left — '}
              {remaining === 0 && <Link to="/signup?plan=pro" style={styles.upgradeLink}>Upgrade →</Link>}
            </span>
          )}
          {pro && <span style={styles.proBadge}>✨ Pro</span>}
        </div>
        <textarea
          style={styles.textarea}
          placeholder="e.g. What's the difference between a Roth IRA and a 401k?"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={handleKey}
          rows={4}
        />
        <button style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }} onClick={askQuestion} disabled={loading}>
          {loading ? '⏳ Thinking...' : 'Ask KnowFi'}
        </button>
        {(answer || loading) && (
          <div style={styles.answerBox}>
            <div style={styles.answerLabel}>🤖 KnowFi says:</div>
            <StreamingText text={answer} loading={loading} style={styles.answer} />
          </div>
        )}
      </div>

      {/* Jargon translator */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardIcon}>📖</span>
          <h2 style={styles.cardTitle}>Jargon Translator</h2>
        </div>
        <p style={styles.cardDesc}>Paste any confusing financial term and we'll explain it in plain English.</p>
        <div style={styles.inputRow}>
          <input
            style={styles.input}
            placeholder="e.g. amortization, EBITDA, P/E ratio, APR..."
            value={term}
            onChange={e => setTerm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && translateJargon()}
          />
          <button style={{ ...styles.btn, ...styles.btnInline, ...(jargonLoading ? styles.btnDisabled : {}) }} onClick={translateJargon} disabled={jargonLoading}>
            {jargonLoading ? '⏳' : 'Translate'}
          </button>
        </div>
        {(explanation || jargonLoading) && (
          <div style={styles.answerBox}>
            <div style={styles.answerLabel}>📖 In plain English:</div>
            <StreamingText text={explanation} loading={jargonLoading} style={styles.answer} />
          </div>
        )}
      </div>

      {/* Headline Translator */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardIcon}>📰</span>
          <h2 style={styles.cardTitle}>Headline Translator</h2>
        </div>
        <p style={styles.cardDesc}>Paste any confusing financial headline. We'll tell you what it actually means for your money.</p>
        <div style={styles.inputRow}>
          <input
            style={styles.input}
            placeholder='e.g. "Fed signals tightening amid sticky inflation"'
            value={headline}
            onChange={e => setHeadline(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && translateHeadline()}
          />
          <button style={{ ...styles.btn, ...styles.btnInline, ...(headlineLoading ? styles.btnDisabled : {}) }} onClick={translateHeadline} disabled={headlineLoading}>
            {headlineLoading ? '⏳' : 'Translate'}
          </button>
        </div>
        {(translation || headlineLoading) && (
          <div style={styles.answerBox}>
            <div style={styles.answerLabel}>📰 What this actually means:</div>
            <StreamingText text={translation} loading={headlineLoading} style={styles.answer} />
          </div>
        )}
        <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {sampleHeadlines.map((h, i) => (
            <button key={i} style={styles.suggestionBtn} onClick={() => setHeadline(h)}>{h}</button>
          ))}
        </div>
      </div>

      {/* Sample questions */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardIcon}>💡</span>
          <h2 style={styles.cardTitle}>Try These</h2>
        </div>
        <div style={styles.suggestions}>
          {suggestions.map((s, i) => (
            <button key={i} style={styles.suggestionBtn} onClick={() => setQuestion(s)}>{s}</button>
          ))}
        </div>
      </div>
      <p style={styles.legalFooter}>
        <strong>Disclaimer:</strong> Responses are AI-generated for educational purposes only and are not financial, investment, tax, or legal advice. Always consult a qualified financial professional before making financial decisions.
      </p>
    </div>
  );
}

const sampleHeadlines = [
  'Fed signals tightening amid sticky inflation',
  'Treasury yields surge as recession fears mount',
  'CPI rose 0.4% last month, beating expectations',
  'Fed holds rates steady, signals two cuts in 2025',
];

const suggestions = [
  "How does compound interest work?",
  "What is a Roth IRA and should I get one?",
  "How much should I have saved by age 30?",
  "What's the S&P 500 and how do I invest in it?",
  "How do I start investing with only $100?",
  "What's the difference between good debt and bad debt?",
];


      {/* Email capture */}
      <div style={{ marginTop: '48px', marginBottom: '24px' }}>
        <EmailCapture variant="card" source="ask" title="Get smarter about money — free." subtitle="The KnowFi Daily Brief: plain English market news every morning." />
      </div>

export default Ask;
